/**
 * Customer services
 * Handles customer CRUD, search, B2B customers, PAN/GST validation, waitlist.
 */
import axios from "axios";
import useReferenceStore from "../stores/useReferenceStore";
import { applyAuthInterceptors } from "./authInterceptor";
import { applyHealthInterceptors } from "../utils/healthReporter";

const retailURL = process.env.REACT_APP_serverUrl;
const retailInstance = axios.create();
retailInstance.defaults.baseURL = retailURL;
retailInstance.defaults.method = "POST";
retailInstance.defaults.headers.post["Content-Type"] = "application/json";

// Dynamic tenant baseURL interceptor
retailInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_serverUrl;
  if (url) config.baseURL = url;
  return config;
});

// Auth interceptors: token attachment + 401 refresh/retry
applyAuthInterceptors(retailInstance);

// Health tracking interceptors (latency + success/failure)
applyHealthInterceptors(retailInstance);

const createCustomerService = async (customerInput) => {
  try {
    const response = await retailInstance({
      data: customerInput,
    });
    return response;
  } catch (error) {
    console.debug(error, "create customer error");
  }
};

const updateCustomerService = async (customerInput) => {
  try {
    const response = await retailInstance({
      data: customerInput,
    });
    return response;
  } catch (error) {
    console.debug(error, "update customer error");
  }
};

const getCustomerService = async (customerInput) => {
  try {
    const response = await retailInstance({
      data: customerInput,
    });
    return response;
  } catch (error) {
    console.debug(error, "get customer error");
  }
};

const customerSearch = async (data) => {
  try {
    const response = await retailInstance({ data });
    return response;
  } catch (error) {
    console.error("Error in customerSearch:", error);
    return null;
  }
};

const customerSearchByName = async (value) => {
  try {
    const queryPayload = {
      query: `query CustomerSearch($name: String) {
          customerSearch(name: $name) {
            cwrCustomerId
            code
            name
            email
            mobileNo
            pincode
            retlLoyaltyBalance
            b2cRegisteredstoreId
            iscredit
            loyaltyLevel {
              cwrLoyaltyLevelId
              name
              accumulationRate
              redemptionRate
            }
            cwrWallet {
              wallet_Id
              balance_Amt
            }
            sCustomer {
              sCustomerID
              customerCategory {
                sCustomerCateforyId
                value
                name
                description
              }
            }
            csBunit {
              csBunitId
              name
            }
            b2cCustomerSegment {
              name
              cwrB2CCustomerSegmentId
            }
          }
        }`,
      variables: { name: value },
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in customer search by name");
  }
};

const getB2BCustomers = async (payload) => {
  try {
    const queryPayload = {
      query: `
        query getB2BCustomerList($q: String, $startRow: String, $endRow: String) {
          getB2BCustomerList(q: $q, startRow: $startRow, endRow: $endRow)
        }
      `,
      variables: {
        q: payload.q ?? "",
        startRow: payload.startRow ?? "0",
        endRow: payload.endRow ?? "20",
      },
    };
    const response = await retailInstance({ data: queryPayload });
    const rawString = response?.data?.data?.getB2BCustomerList || response?.data?.getB2BCustomerList;

    if (!rawString) {
      console.warn("NO rawString — response missing getB2BCustomerList");
      return null;
    }

    let parsed;
    try {
      parsed = JSON.parse(rawString);
    } catch (e) {
      console.error("JSON PARSE ERROR →", e);
      return null;
    }

    const customers = parsed.customers || [];
    const result = { data: { getB2BCustomerList: { customers } } };
    return result;
  } catch (error) {
    console.error("getB2BCustomerList ERROR →", error);
    return null;
  }
};

const validatePANNo = async (panNumber) => {
  const queryPayload = {
    query: `
      mutation ValidatePAN($panNo: String) {
        validatePANNumber(panNo: $panNo)
      }
    `,
    variables: { panNo: panNumber },
  };

  try {
    const response = await retailInstance({ data: queryPayload });
    const rawData = response?.data?.data?.validatePANNumber;
    if (rawData === undefined || rawData === null) throw new Error("validatePANNumber returned no data");
    return JSON.parse(rawData);
  } catch (error) {
    console.error("Error validating PAN:", error);
    throw error;
  }
};

const validateGSTNumber = async (gstNo) => {
  const queryPayload = {
    query: `
      mutation ValidateGST($gstNo: String) {
        validateGSTNumber(gstNo: $gstNo)
      }
    `,
    variables: { gstNo },
  };

  try {
    const response = await retailInstance({ data: queryPayload });
    const rawData = response?.data?.data?.validateGSTNumber;
    if (rawData === undefined || rawData === null) throw new Error("validateGSTNumber returned no data");
    return JSON.parse(rawData);
  } catch (error) {
    console.error("Error validating GST:", error);
    throw error;
  }
};

const getCustomerConfig = async (syncId) => {
  try {
    const queryPayload = {
      query: `query {
        getRetailCustomerConfig(bunitId: null, limit: 50, offset: 0)
      }`,
    };

    const response = await retailInstance({ data: queryPayload });

    const rawData = response.data.data.getRetailCustomerConfig;
    const customerConfig = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
    console.debug("customerConfig", customerConfig);
    if (customerConfig?.customerConfigs?.length) {
      useReferenceStore.getState().setCustomerConfigs(customerConfig.customerConfigs);
    }

    return customerConfig;
  } catch (error) {
    console.error("Error:", error);
  }
};

const getWaitlist = async (startRow = "0", endRow = "10") => {
  try {
    const queryPayload = {
      query: `query {
        getWaitlist(startRow: "${startRow}", endRow: "${endRow}")
      }`,
    };

    const response = await retailInstance({ data: queryPayload });

    const rawData = response?.data?.data?.getWaitlist;
    console.debug("rawData →", rawData);

    const waitlistData = typeof rawData === "string" ? JSON.parse(rawData) : rawData;

    console.debug("waitlistData →", waitlistData);

    return waitlistData?.Waitlist ?? [];
  } catch (error) {
    console.error("Error fetching waitlist:", error);
    return [];
  }
};

const upsertWaitlist = async (payload) => {
  try {
    const requestString = JSON.stringify(payload);

    const queryPayload = {
      query: `mutation {
        upsertWaitlist(
          request: """${requestString}"""
        ) {
          status
          message
        }
      }`,
    };

    const response = await retailInstance({ data: queryPayload });

    const result = response?.data?.data?.upsertWaitlist;
    console.debug("Upsert Result →", result);

    return result;
  } catch (error) {
    console.error("Error in upsertWaitlist:", error);
    return { status: "500", message: "Something went wrong" };
  }
};

export {
  createCustomerService,
  updateCustomerService,
  getCustomerService,
  customerSearch,
  customerSearchByName,
  getB2BCustomers,
  validatePANNo,
  validateGSTNumber,
  getCustomerConfig,
  getWaitlist,
  upsertWaitlist,
};
